import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useI18n } from '@/context/LanguageContext';
import { Logo } from '@/components/Logo';
import { Separator } from '@/components/ui/separator';

export function Footer() {
  const { t, lang } = useI18n();
  const company = t('footer.company');

  return (
    <footer className="border-t border-border/70 bg-[hsl(var(--inv-surface))]">
      <div className="mx-auto max-w-[1120px] px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {t('footer.about')}
            </p>
          </div>

          <div className="md:col-span-4">
            <h3 className="font-heading text-sm font-semibold text-[hsl(var(--inv-slate))]">
              {t('footer.companyTitle')}
            </h3>
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              {Array.isArray(company) &&
                company.map((line, i) => (
                  <li key={i} className="font-mono text-[13px]">
                    {line}
                  </li>
                ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="font-heading text-sm font-semibold text-[hsl(var(--inv-slate))]">
              {t('footer.contactTitle')}
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--inv-terracotta))]" />
                <span>{t('contact.address')}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-[hsl(var(--inv-terracotta))]" />
                <a
                  href={`mailto:${t('contact.email')}`}
                  data-testid="footer-email-link"
                  className="transition-colors hover:text-[hsl(var(--inv-blue))]"
                >
                  {t('contact.email')}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-[hsl(var(--inv-terracotta))]" />
                <a
                  href={`tel:${t('contact.phone').replace(/\s/g, '')}`}
                  data-testid="footer-phone-link"
                  className="transition-colors hover:text-[hsl(var(--inv-blue))]"
                >
                  {t('contact.phone')}
                </a>
              </li>
            </ul>

            <nav className="mt-6 flex flex-col gap-2 text-sm">
              <Link
                to={`/${lang}/privacy`}
                data-testid="footer-privacy-link"
                className="text-[hsl(var(--inv-slate))] transition-colors hover:text-[hsl(var(--inv-blue))]"
              >
                {t('footer.links.privacy')}
              </Link>
              <Link
                to={`/${lang}/cookies`}
                data-testid="footer-cookies-link"
                className="text-[hsl(var(--inv-slate))] transition-colors hover:text-[hsl(var(--inv-blue))]"
              >
                {t('footer.links.cookies')}
              </Link>
              <Link
                to={`/${lang}/terms`}
                data-testid="footer-terms-link"
                className="text-[hsl(var(--inv-slate))] transition-colors hover:text-[hsl(var(--inv-blue))]"
              >
                {t('footer.links.terms')}
              </Link>
            </nav>
          </div>
        </div>

        <Separator className="my-8" />

        <p className="text-xs leading-relaxed text-muted-foreground">{t('footer.disclaimer')}</p>
        <p className="mt-3 text-xs text-muted-foreground">{t('footer.rights')}</p>
      </div>
    </footer>
  );
}

export default Footer;
